import Grid from "@mui/material/Grid";
import { TextField } from "components/TextField";
import { Autocomplete } from "components/Autocomplete";
import React from "react";
import { Dialog } from "components/Dialog";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Tag } from "interfaces/tag";

const validationSchema = Yup.object({
  ruleName: Yup.string()
    .label("Rule name")
    .required(),
  tag: Yup.object()
    .label("Tag")
    .required()
    .nullable(),
  hysteresisPoints: Yup.number()
    .integer()
    .positive()
    .label("Hysteresis points")
    .required()
    .min(1)
});

export interface RuleFormValues {
  ruleName: string;
  tag: Tag | null;
  hysteresisPoints: string;
}

export interface RuleDialogProps {
  open: boolean;
  loading?: boolean;
  error?: boolean;
  errorText?: string;
  title: string;
  initialValues: RuleFormValues;
  tags: Tag[];
  disabledTag?: boolean;
  confirmText: string;
  onSubmit: (values: RuleFormValues) => void;
  onClose: () => void;
}

export const RuleDialog = ({
  open,
  loading = false,
  error = false,
  errorText = "An error occurred",
  title,
  initialValues,
  tags,
  disabledTag = false,
  confirmText,
  onClose,
  onSubmit
}: RuleDialogProps): JSX.Element => {
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      onSubmit(values);
    },
    enableReinitialize: true
  });

  const handleClose = (): void => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog
      dialogId="dialog-rule"
      open={open}
      title={title}
      actionsDisabled={loading}
      onClose={handleClose}
      onSubmit={formik.handleSubmit}
      confirmText={confirmText}
      errorText={error ? errorText : undefined}
      size="medium"
      allowClickaway
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            id="ruleName"
            name="ruleName"
            label="Rule name"
            fullWidth
            required
            disabled={loading}
            value={formik.values.ruleName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.ruleName && Boolean(formik.errors.ruleName)}
            helperText={formik.touched.ruleName && formik.errors.ruleName}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            label="Tag"
            name="tag"
            id="tag"
            required
            loading={loading}
            options={tags}
            disabled={disabledTag}
            isOptionEqualToValue={(option: Tag, value: Tag) =>
              option.id === value.id
            }
            value={formik.values.tag}
            onChange={(e: React.ChangeEvent<{}>, value: Tag) =>
              formik.setFieldValue("tag", value)
            }
            onBlur={formik.handleBlur}
            error={formik.touched.tag && Boolean(formik.errors.tag)}
            getOptionLabel={(tag: Tag) => tag.name}
            // Need this because the tag name may not be unique.
            // E.g. Different sites may have the same tag, but they have different IDs
            // https://github.com/mui/material-ui/blob/master/packages/mui-material/src/Autocomplete/Autocomplete.js#L555C51-L555C51
            renderOption={(props: Object, option: Tag) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            helperText={formik.touched.tag ? formik.errors.tag : undefined}
            placeholder={undefined}
            size={undefined}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Hysteresis Points"
            name="hysteresisPoints"
            id="hysteresisPoints"
            type="number"
            fullWidth
            required
            disabled={loading}
            value={formik.values.hysteresisPoints}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.hysteresisPoints &&
              Boolean(formik.errors.hysteresisPoints)
            }
            helperText={
              formik.touched.hysteresisPoints && formik.errors.hysteresisPoints
            }
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
